import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, errorMessage } from "../lib/api";
import { newEventId, trackMetaEventWithId } from "../lib/analytics";
import { X, Handshake, CheckCircle2 } from "lucide-react";

const TIERS = ["Gold", "Silver", "Bronze", "Community", "Not sure yet"];
const empty = { company_name: "", contact_name: "", email: "", phone: "", interested_tier: "", message: "" };

export const openSponsorForm = () => window.dispatchEvent(new Event("open-sponsor-form"));

/** Site-wide sponsor lead form. Mounted once (in Navbar); opened via openSponsorForm(). */
export default function SponsorInquiryModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = () => { setForm(empty); setDone(false); setOpen(true); };
    window.addEventListener("open-sponsor-form", handler);
    return () => window.removeEventListener("open-sponsor-form", handler);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      const eventId = newEventId();
      const { data } = await api.post("/sponsors/inquiry", { ...form, interested_tier: form.interested_tier === "Not sure yet" ? "" : form.interested_tier, meta_event_id: eventId });
      // Meta 'Lead' — fired only after the enquiry saved successfully.
      if (data?.meta_event_id) trackMetaEventWithId(data.meta_event_name || "Lead", data.meta_event_id, { content_name: "IdeaThon 2026 Sponsorship Interest", content_category: "Sponsorship Enquiry" });
      setDone(true);
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(errorMessage(err));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" data-testid="sponsor-inquiry-modal" onClick={() => setOpen(false)}>
      <div className="relative w-full max-w-lg my-auto bg-[#0A0F1E] border border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white" data-testid="sponsor-modal-close"><X className="w-5 h-5" /></button>
        {done ? (
          <div className="p-10 text-center" data-testid="sponsor-inquiry-success">
            <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-white mt-4">Thank you!</h3>
            <p className="text-gray-400 mt-2 text-sm">We've received your interest in sponsoring IdeaThon. Our team will reach out to you shortly.</p>
            <button onClick={() => setOpen(false)} className="btn-primary mt-6" data-testid="sponsor-success-close">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 space-y-4">
            <div className="flex items-center gap-2 text-brand"><Handshake className="w-5 h-5" strokeWidth={1.5} /><span className="label-eyebrow">Sponsor IdeaThon</span></div>
            <h3 className="font-display text-2xl text-white leading-tight">Put your brand in front of the next generation of builders</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-eyebrow block mb-1.5">Organization <span className="text-brand">*</span></label>
                <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="input-field" placeholder="Company name" required data-testid="inquiry-company" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Your Name <span className="text-brand">*</span></label>
                <input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="input-field" placeholder="Full name" required data-testid="inquiry-name" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Email <span className="text-brand">*</span></label>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-field" placeholder="you@company.com" required data-testid="inquiry-email" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Phone <span className="text-gray-500">(optional)</span></label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-field" placeholder="Phone / WhatsApp" data-testid="inquiry-phone" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Interested Tier <span className="text-gray-500">(optional)</span></label>
              <select value={form.interested_tier} onChange={(e) => set("interested_tier", e.target.value)} className="input-field" data-testid="inquiry-tier">
                <option value="">Select a tier</option>
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Message <span className="text-gray-500">(optional)</span></label>
              <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className="input-field" placeholder="Tell us how you'd like to support IdeaThon…" data-testid="inquiry-message" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="inquiry-submit">
              {busy ? <span className="spinner" /> : "Submit Sponsorship Interest"}
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
